import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Building2,
  ArrowLeft,
  ArrowRight,
  Users,
  Target,
  Heart,
  Shield,
  Zap,
  Globe,
  Award,
  CheckCircle,
  Star,
  Clock,
  Lock,
  Eye,
  FileText,
  Sparkles,
  Lightbulb,
  Rocket,
  Mail,
  MapPin,
  Linkedin,
  Twitter,
} from "lucide-react";

export default function AboutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("mission");

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash) {
      // Use startTransition to avoid cascading render warning
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
      // Defer state update to next tick
      requestAnimationFrame(() => {
        setActiveSection(hash);
      });
    }
  }, [location]);

  const sections = [
    { id: "mission", title: "Our Mission", icon: Target },
    { id: "problem", title: "The Problem", icon: Lightbulb },
    { id: "features", title: "What We Built", icon: Rocket },
    { id: "team", title: "Our Team", icon: Users },
    { id: "security", title: "Security & Privacy", icon: Shield },
    { id: "contact", title: "Contact Us", icon: Mail },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/docs")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Docs
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-slate-800">About Us</span>
              </div>
            </div>
            <Button onClick={() => navigate("/login")}>
              Get Started
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-[280px,1fr] gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-800 mb-4">Contents</h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSection === section.id
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <section.icon className="w-4 h-4" />
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="min-w-0">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-8 md:p-12">
                {/* Hero */}
                <section id="mission" className="mb-16">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-slate-800">About MyMeetingsPal</h1>
                      <p className="text-slate-500">Our story, mission, and team</p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-8 mb-8 text-white">
                    <h2 className="text-2xl font-bold mb-4">Our Mission</h2>
                    <p className="text-lg text-blue-100 leading-relaxed">
                      To transform how organizations capture, manage, and leverage their corporate meetings — 
                      making professional governance accessible to every company, from ambitious startups 
                      to established public corporations.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-xl p-5 text-center">
                      <Heart className="w-8 h-8 text-rose-500 mx-auto mb-2" />
                      <h4 className="font-semibold text-slate-800">User-Centric</h4>
                      <p className="text-sm text-slate-600 mt-1">Built by governance professionals for governance professionals</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-5 text-center">
                      <Zap className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <h4 className="font-semibold text-slate-800">AI-Powered</h4>
                      <p className="text-sm text-slate-600 mt-1">Cutting-edge AI that actually saves you hours</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-5 text-center">
                      <Shield className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                      <h4 className="font-semibold text-slate-800">Compliance-First</h4>
                      <p className="text-sm text-slate-600 mt-1">Enterprise security and governance built-in</p>
                    </div>
                  </div>
                </section>

                {/* The Problem */}
                <section id="problem" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Lightbulb className="w-6 h-6 text-amber-500" />
                    The Problem We Set Out to Solve
                  </h2>

                  <div className="prose prose-slate max-w-none">
                    <p className="text-lg text-slate-600 leading-relaxed">
                      Every company — whether a two-person startup or a multinational corporation — has meetings. 
                      And for many of those meetings, someone needs to capture what happened. It sounds simple, 
                      but the reality is anything but.
                    </p>

                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 my-8">
                      <h3 className="text-lg font-semibold text-amber-900 mb-3">The Traditional Approach</h3>
                      <ul className="space-y-2 text-amber-800">
                        <li className="flex items-start gap-3">
                          <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <span><strong>4-8 hours</strong> spent per board meeting on minute preparation</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <span>Manual transcription, formatting, and chasing attendees for sign-off</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Eye className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <span>Version control nightmares with Word documents emailed back and forth</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Lock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <span>No audit trail, no immutable records, compliance anxiety</span>
                        </li>
                      </ul>
                    </div>

                    <p>
                      Our founders experienced this firsthand. Mark spent 15 years as a corporate governance 
                      consultant, watching clients struggle with the same problems over and over. Sarah saw 
                      the potential for AI to transform document intelligence at Google. Together, they 
                      realized that corporate minutes were ripe for disruption.
                    </p>

                    <blockquote className="border-l-4 border-blue-500 pl-6 my-8 italic text-slate-600">
                      &ldquo;We kept seeing companies — even sophisticated public companies — using the same 
                      inefficient processes. There had to be a better way. We built MyMeetingsPal to be 
                      that better way.&rdquo;
                      <footer className="mt-2 text-sm not-italic text-slate-500">— Mark Richardson, CEO</footer>
                    </blockquote>
                  </div>
                </section>

                {/* What We Built */}
                <section id="features" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Rocket className="w-6 h-6 text-purple-500" />
                    What We Built
                  </h2>

                  <div className="prose prose-slate max-w-none mb-8">
                    <p className="text-lg">
                      MyMeetingsPal is a comprehensive corporate minutes platform that combines AI automation 
                      with governance-grade compliance features. Here&apos;s what makes us different:
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-6">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                        <Zap className="w-5 h-5 text-teal-600" />
                      </div>
                      <h3 className="font-semibold text-slate-800 mb-2">AI-Powered Transcription</h3>
                      <p className="text-sm text-slate-600">
                        Upload your meeting recording and get accurate transcripts in minutes, not hours. 
                        Our AI identifies speakers and extracts key discussion points automatically.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-slate-800 mb-2">Smart Minutes Generation</h3>
                      <p className="text-sm text-slate-600">
                        One click generates professionally formatted corporate minutes, complete with 
                        resolutions, action items, and attendance records.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-xl p-6">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                        <Shield className="w-5 h-5 text-purple-600" />
                      </div>
                      <h3 className="font-semibold text-slate-800 mb-2">Governance Suite</h3>
                      <p className="text-sm text-slate-600">
                        Chair digital signing, document locking, audit trails, governance linting — 
                        everything public companies need for compliance confidence.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                        <CheckCircle className="w-5 h-5 text-amber-600" />
                      </div>
                      <h3 className="font-semibold text-slate-800 mb-2">Action Item Tracking</h3>
                      <p className="text-sm text-slate-600">
                        Never lose track of action items again. Automatic extraction, cross-meeting 
                        registers, carry-forward to &ldquo;Matters Arising&rdquo;, and deadline notifications.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Our Team */}
                <section id="team" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Users className="w-6 h-6 text-blue-500" />
                    Meet Our Team
                  </h2>

                  <div className="prose prose-slate max-w-none mb-8">
                    <p>
                      We&apos;re a team of governance professionals, AI researchers, and enterprise software 
                      veterans united by a shared belief: corporate governance shouldn&apos;t be painful.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="bg-slate-50 rounded-xl p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-2xl font-bold text-white">
                          MR
                        </div>
                        <div>
                          <div className="font-bold text-slate-800 text-lg">Mark Richardson</div>
                          <div className="text-sm text-blue-600 font-medium">CEO & Co-Founder</div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mb-4">
                        15 years in corporate governance consulting. Former Company Secretary at 
                        an ASX-listed company. Passionate about making governance accessible.
                      </p>
                      <div className="flex items-center gap-2">
                        <a href="#" className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center hover:bg-slate-300 transition-colors">
                          <Linkedin className="w-4 h-4 text-slate-600" />
                        </a>
                        <a href="#" className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center hover:bg-slate-300 transition-colors">
                          <Twitter className="w-4 h-4 text-slate-600" />
                        </a>
                      </div>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-2xl font-bold text-white">
                          ST
                        </div>
                        <div>
                          <div className="font-bold text-slate-800 text-lg">Sarah Thompson</div>
                          <div className="text-sm text-emerald-600 font-medium">CTO & Co-Founder</div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mb-4">
                        Ex-Google AI researcher specializing in NLP and document intelligence. 
                        PhD in Computational Linguistics from Stanford.
                      </p>
                      <div className="flex items-center gap-2">
                        <a href="#" className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center hover:bg-slate-300 transition-colors">
                          <Linkedin className="w-4 h-4 text-slate-600" />
                        </a>
                        <a href="#" className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center hover:bg-slate-300 transition-colors">
                          <Twitter className="w-4 h-4 text-slate-600" />
                        </a>
                      </div>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center text-2xl font-bold text-white">
                          JL
                        </div>
                        <div>
                          <div className="font-bold text-slate-800 text-lg">James Liu</div>
                          <div className="text-sm text-amber-600 font-medium">VP Engineering</div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mb-4">
                        10 years building enterprise SaaS platforms. Former engineering lead at 
                        DocuSign. Expert in security-critical applications.
                      </p>
                      <div className="flex items-center gap-2">
                        <a href="#" className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center hover:bg-slate-300 transition-colors">
                          <Linkedin className="w-4 h-4 text-slate-600" />
                        </a>
                      </div>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center text-2xl font-bold text-white">
                          AP
                        </div>
                        <div>
                          <div className="font-bold text-slate-800 text-lg">Amanda Park</div>
                          <div className="text-sm text-rose-600 font-medium">VP Sales</div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mb-4">
                        8 years in enterprise SaaS sales. Deep relationships in the governance 
                        and compliance community. Customer success obsessed.
                      </p>
                      <div className="flex items-center gap-2">
                        <a href="#" className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center hover:bg-slate-300 transition-colors">
                          <Linkedin className="w-4 h-4 text-slate-600" />
                        </a>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Security */}
                <section id="security" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Shield className="w-6 h-6 text-emerald-500" />
                    Security & Privacy
                  </h2>

                  <div className="prose prose-slate max-w-none mb-8">
                    <p className="text-lg">
                      We understand that your meeting content is sensitive. That&apos;s why security isn&apos;t 
                      an afterthought — it&apos;s foundational to everything we build.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                      <Lock className="w-6 h-6 text-emerald-600 mb-3" />
                      <h4 className="font-semibold text-slate-800 mb-2">Encryption at Rest & Transit</h4>
                      <p className="text-sm text-slate-600">
                        All data encrypted with AES-256 at rest and TLS 1.3 in transit. 
                        Your documents are always protected.
                      </p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                      <Shield className="w-6 h-6 text-emerald-600 mb-3" />
                      <h4 className="font-semibold text-slate-800 mb-2">SOC 2 Compliant</h4>
                      <p className="text-sm text-slate-600">
                        Our infrastructure and processes are designed for SOC 2 Type II 
                        compliance, with certification in progress.
                      </p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                      <Eye className="w-6 h-6 text-emerald-600 mb-3" />
                      <h4 className="font-semibold text-slate-800 mb-2">Complete Audit Trails</h4>
                      <p className="text-sm text-slate-600">
                        Every action is logged. Know who viewed, edited, or exported any 
                        document, and when.
                      </p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                      <Globe className="w-6 h-6 text-emerald-600 mb-3" />
                      <h4 className="font-semibold text-slate-800 mb-2">Data Residency Options</h4>
                      <p className="text-sm text-slate-600">
                        Enterprise customers can choose their data region to meet local 
                        compliance requirements.
                      </p>
                    </div>
                  </div>

                  <div className="bg-slate-100 rounded-xl p-6 mt-6">
                    <h4 className="font-semibold text-slate-800 mb-3">Our Security Commitments</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-3 text-slate-700">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span>We never use your data to train our AI models</span>
                      </li>
                      <li className="flex items-center gap-3 text-slate-700">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span>Audio files are deleted after transcription (configurable)</span>
                      </li>
                      <li className="flex items-center gap-3 text-slate-700">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span>Regular third-party security audits and penetration testing</span>
                      </li>
                      <li className="flex items-center gap-3 text-slate-700">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span>GDPR compliant with data processing agreements available</span>
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Contact */}
                <section id="contact" className="mb-8 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Mail className="w-6 h-6 text-blue-500" />
                    Get in Touch
                  </h2>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h4 className="font-semibold text-slate-800 mb-4">General Inquiries</h4>
                      <div className="space-y-3">
                        <a href="mailto:hello@mymeetingspal.com" className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors">
                          <Mail className="w-5 h-5" />
                          hello@mymeetingspal.com
                        </a>
                        <a href="tel:+1-888-555-0123" className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors">
                          <Globe className="w-5 h-5" />
                          +1 (888) 555-0123
                        </a>
                        <div className="flex items-center gap-3 text-slate-600">
                          <MapPin className="w-5 h-5" />
                          Sydney, Australia
                        </div>
                      </div>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-6">
                      <h4 className="font-semibold text-slate-800 mb-4">Other Contacts</h4>
                      <div className="space-y-3">
                        <a href="mailto:sales@mymeetingspal.com" className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors">
                          <Mail className="w-5 h-5" />
                          sales@mymeetingspal.com
                        </a>
                        <a href="mailto:support@mymeetingspal.com" className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors">
                          <Mail className="w-5 h-5" />
                          support@mymeetingspal.com
                        </a>
                        <a href="mailto:investors@mymeetingspal.com" className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors">
                          <Mail className="w-5 h-5" />
                          investors@mymeetingspal.com
                        </a>
                      </div>
                    </div>
                  </div>
                </section>

                {/* CTA */}
                <div className="mt-12 p-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl text-center">
                  <Sparkles className="w-12 h-12 text-blue-200 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">Ready to Transform Your Meetings?</h3>
                  <p className="text-blue-100 mb-6">
                    Join thousands of governance teams using MyMeetingsPal.
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      onClick={() => navigate("/login")}
                      className="bg-white text-blue-600 hover:bg-blue-50"
                    >
                      Start Free Trial
                    </Button>
                    <Button
                      variant="outline"
                      className="border-white text-white hover:bg-blue-600"
                      onClick={() => navigate("/pricing")}
                    >
                      View Pricing
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
